import React, { useState } from 'react';
import { Card, Badge } from '@tracekit/core';
import { DollarSign, Calendar, Users, Building2, ChevronRight } from 'lucide-react';

interface Opportunity {
  id: string;
  title: string;
  value?: number;
  deadline?: string;
  organization?: string;
  status: string;
  probability?: number;
}

interface KanbanColumn {
  id: string;
  title: string;
  count: number;
  value: number;
  opportunities: Opportunity[];
  color: string;
}

interface OpportunityKanbanProps {
  opportunities: any[];
  onOpportunityClick?: (id: string) => void;
}

export const OpportunityKanban: React.FC<OpportunityKanbanProps> = ({
  opportunities,
  onOpportunityClick,
}) => {
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  // Group opportunities by stage
  const columns: KanbanColumn[] = [
    {
      id: 'identified',
      title: 'Identified',
      count: 0,
      value: 0,
      opportunities: [],
      color: 'bg-blue-500',
    },
    {
      id: 'qualified',
      title: 'Qualified',
      count: 0,
      value: 0,
      opportunities: [],
      color: 'bg-cyan-500',
    },
    {
      id: 'proposal',
      title: 'Proposal',
      count: 0,
      value: 0,
      opportunities: [],
      color: 'bg-yellow-500',
    },
    {
      id: 'negotiation',
      title: 'Negotiation',
      count: 0,
      value: 0,
      opportunities: [],
      color: 'bg-orange-500',
    },
    {
      id: 'won',
      title: 'Won',
      count: 0,
      value: 0,
      opportunities: [],
      color: 'bg-green-500',
    },
  ];

  // Distribute opportunities across columns (simplified logic)
  opportunities.forEach((opp, idx) => {
    const columnIdx = idx % 5;
    const value = opp.data?.value || opp.data?.estimated_value || 0;
    columns[columnIdx].opportunities.push({
      id: opp.id,
      title: opp.data?.title || 'Untitled Opportunity',
      value,
      deadline: opp.data?.deadline,
      organization: opp.data?.organization,
      status: opp.data?.status || 'active',
      probability: opp.data?.win_probability || 50,
    });
    columns[columnIdx].count++;
    columns[columnIdx].value += value;
  });

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No deadline';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 30) return `${diffDays}d left`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="w-full">
      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((column) => (
          <div
            key={column.id}
            className="flex-shrink-0 w-80"
            style={{ minWidth: '320px' }}
          >
            {/* Column Header */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${column.color}`} />
                  <h3 className="text-sm font-semibold text-theme-text">
                    {column.title}
                  </h3>
                  <Badge variant="default" className="text-xs">
                    {column.count}
                  </Badge>
                </div>
              </div>
              <div className="text-xs text-theme-text-secondary font-medium">
                {formatCurrency(column.value)} total value
              </div>
            </div>

            {/* Cards Container */}
            <div className="space-y-3 min-h-[200px]">
              {column.opportunities.slice(0, 10).map((opp) => (
                <Card
                  key={opp.id}
                  className="p-4 cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                  onClick={() => onOpportunityClick?.(opp.id)}
                >
                  {/* Title */}
                  <h4 className="text-sm font-semibold text-theme-text mb-2 line-clamp-2">
                    {opp.title}
                  </h4>

                  {/* Organization */}
                  {opp.organization && (
                    <div className="flex items-center gap-1 text-xs text-theme-text-secondary mb-2">
                      <Building2 className="w-3 h-3" />
                      <span className="truncate">{opp.organization}</span>
                    </div>
                  )}

                  {/* Value & Probability */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1 text-sm font-bold text-green-600 dark:text-green-400">
                      <DollarSign className="w-4 h-4" />
                      {opp.value ? formatCurrency(opp.value) : 'N/A'}
                    </div>
                    {opp.probability && (
                      <Badge
                        variant={opp.probability >= 70 ? 'success' : opp.probability >= 40 ? 'warning' : 'default'}
                        className="text-xs"
                      >
                        {opp.probability}% win
                      </Badge>
                    )}
                  </div>

                  {/* Deadline */}
                  <div className="flex items-center gap-1 text-xs text-theme-text-secondary">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(opp.deadline)}</span>
                  </div>

                  {/* Action Arrow */}
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <ChevronRight className="w-4 h-4 text-theme-text-secondary" />
                  </div>
                </Card>
              ))}

              {column.opportunities.length === 0 && (
                <div className="text-center py-8 text-sm text-theme-text-secondary">
                  No opportunities
                </div>
              )}

              {column.opportunities.length > 10 && (
                <div className="text-center py-2 text-xs text-theme-text-secondary">
                  +{column.opportunities.length - 10} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

